import React, { useState } from 'react';
import axios from 'axios';
import { X } from 'lucide-react';

const ACTION_LABELS = {
  confirmation: 'Confirm Employee',
  promotion: 'Promotion',
  transfer: 'Transfer',
  salary_revision: 'Salary Revision',
  role_change: 'Role Change',
  reporting_change: 'Reporting Change',
  resignation: 'Resignation',
  termination: 'Termination',
  exit_clearance: 'Exit Clearance',
};

const LifecycleActionModal = ({ isOpen, onClose, employeeId, action, masters, onSuccess, searchManagers }) => {
  const [form, setForm] = useState({ effective_date: '', remarks: '', reason: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [managers, setManagers] = useState([]);

  React.useEffect(() => {
    if (isOpen && searchManagers) {
      searchManagers('').then(setManagers);
    }
  }, [isOpen, searchManagers]);

  if (!isOpen || !action) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setLoading(true);
    setError('');
    try {
      await axios.post(`/v1/employee-master/${employeeId}/lifecycle/${action}`, form);
      onSuccess?.();
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || 'Action failed');
    } finally {
      setLoading(false);
    }
  };

  const select = (label, field, options, valueKey = 'value', labelKey = 'label') => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <select
        value={form[field] || ''}
        onChange={(e) => set(field, e.target.value ? Number(e.target.value) || e.target.value : '')}
        className="w-full border rounded-md px-3 py-2"
      >
        <option value="">Select…</option>
        {(options || []).map((o) => (
          <option key={o[valueKey]} value={o[valueKey]}>{o[labelKey]}</option>
        ))}
      </select>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-semibold">{ACTION_LABELS[action] || action}</h3>
          <button type="button" onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 space-y-3">
          {error && <p className="text-red-600 text-sm">{error}</p>}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Effective Date</label>
            <input type="date" value={form.effective_date} onChange={(e) => set('effective_date', e.target.value)} className="w-full border rounded-md px-3 py-2" />
          </div>
          {action === 'promotion' && (
            <>
              {select('New Grade', 'grade_master_id', masters?.grades)}
              {select('New Role', 'hospital_role_id', masters?.roles, 'value', 'label')}
              {select('Department', 'department_id', masters?.departments)}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Basic Salary</label>
                <input type="number" value={form.basic_salary || ''} onChange={(e) => set('basic_salary', e.target.value)} className="w-full border rounded-md px-3 py-2" />
              </div>
            </>
          )}
          {action === 'transfer' && (
            <>
              {select('Department', 'department_id', masters?.departments)}
              {select('Shift Group', 'shift_group_id', masters?.shiftGroups?.map((g) => ({ value: g.id, label: g.name })))}
              {select('Reporting Manager', 'reporting_manager_id', managers.map((m) => ({ value: m.id, label: m.name })))}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Branch</label>
                <select value={form.hospital_branch || ''} onChange={(e) => set('hospital_branch', e.target.value)} className="w-full border rounded-md px-3 py-2">
                  <option value="">Select branch</option>
                  {(masters?.branches || []).map((b) => (
                    <option key={b.value || b.id} value={b.label || b.name}>{b.label || b.name}</option>
                  ))}
                </select>
              </div>
            </>
          )}
          {action === 'salary_revision' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Basic Salary</label>
                <input type="number" value={form.basic_salary || ''} onChange={(e) => set('basic_salary', e.target.value)} className="w-full border rounded-md px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Grade Pay</label>
                <input type="number" value={form.grade_pay || ''} onChange={(e) => set('grade_pay', e.target.value)} className="w-full border rounded-md px-3 py-2" />
              </div>
            </>
          )}
          {action === 'resignation' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Last Working Day</label>
              <input type="date" value={form.last_working_day || ''} onChange={(e) => set('last_working_day', e.target.value)} className="w-full border rounded-md px-3 py-2" />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
            <input type="text" value={form.reason || ''} onChange={(e) => set('reason', e.target.value)} className="w-full border rounded-md px-3 py-2" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
            <textarea value={form.remarks || ''} onChange={(e) => set('remarks', e.target.value)} rows={2} className="w-full border rounded-md px-3 py-2" />
          </div>
        </div>
        <div className="p-4 border-t flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 border rounded-md">Cancel</button>
          <button type="button" onClick={submit} disabled={loading} className="px-4 py-2 bg-blue-600 text-white rounded-md disabled:opacity-50">
            {loading ? 'Saving…' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LifecycleActionModal;
